import { useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGetWallet } from '@/domain'

import { StickyHeaderNavigation } from '@/components'
import { CryptoSwapInputs } from '@/features/conversion/components/CryptoSwapInputs'
import { ConversionInfos } from '@/features/conversion/components/ConversionInfos'
import { ConfirmationButton } from '@/features/conversion/components/ConfirmationButton'
import { useConversionScopedStore } from '@/store/scoped/useConversionScopedStore'
import { useUserStore } from '@/store'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { BaseSafeFormView, BaseView } from '@/components/base'

import type { CryptoCode, FiatCode } from '@/constants/currencyCodes'
import useOffsets from '@/hooks/useOffsets'
import { useAppAnalytics } from '@/services/analytics'

export interface ConversionScreenParams {
  creditAmount?: string
  creditFiatAmount?: string
  creditCurrencyCode?: CryptoCode

  debitAmount?: string
  debitFiatAmount?: string
  debitCurrencyCode?: CryptoCode
}

const DEFAULT_DEBIT_AMOUNT = '0'
const DEFAULT_DEBIT_FIAT_AMOUNT = '0'
const DEFAULT_DEBIT_CURRENCY_CODE = 'BTC' as CryptoCode

const DEFAULT_CREDIT_AMOUNT = '0'
const DEFAULT_CREDIT_FIAT_AMOUNT = '0'
const DEFAULT_CREDIT_CURRENCY_CODE = 'USDT' as CryptoCode

export const ConversionScreen = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const offsets = useOffsets()
  const { trackEvent, events } = useAppAnalytics()

  // Get parameters from location state (passed from CryptoActions)
  const locationState = location.state as ConversionScreenParams | null
  const debitCurrencyCode =
    locationState?.debitCurrencyCode || DEFAULT_DEBIT_CURRENCY_CODE
  const creditCurrencyCode =
    locationState?.creditCurrencyCode || DEFAULT_CREDIT_CURRENCY_CODE

  const debitAmount = DEFAULT_DEBIT_AMOUNT
  const debitFiatAmount = DEFAULT_DEBIT_FIAT_AMOUNT
  const creditAmount = DEFAULT_CREDIT_AMOUNT
  const creditFiatAmount = DEFAULT_CREDIT_FIAT_AMOUNT

  const { data: walletData } = useGetWallet()
  const userWallet = useUserStore((state) => state.wallet?.completeCurrencies)
  const completeCurrencies = walletData?.completeCurrencies || userWallet
  const setConversionFromStore = useConversionScopedStore(
    (state) => state.setConversion,
  )
  const setIsFiatMode = useConversionScopedStore((state) => state.setIsFiatMode)
  const displayCurrency = useAppPreferencesStore(
    (state) => state.displayCurrency,
  )

  // Stabilize setConversion function
  const setConversion = useCallback(setConversionFromStore, [
    setConversionFromStore,
  ])

  // Try to find currencies in wallet, otherwise use defaults from store
  const defaultDebitWallet =
    (completeCurrencies || []).find(
      (currency) => currency.code === debitCurrencyCode,
    ) ||
    useConversionScopedStore.getState().conversion.from.currencyInfoInWallet
  const defaultCreditWallet =
    (completeCurrencies || []).find(
      (currency) => currency.code === creditCurrencyCode,
    ) || useConversionScopedStore.getState().conversion.to.currencyInfoInWallet

  // SET INITIAL CONVERSION INFORMED BY PARAMS
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (defaultDebitWallet && defaultCreditWallet) {
      setIsFiatMode(true)
      setConversion({
        from: {
          fiatAmount: {
            amount: debitFiatAmount,
            currency: (displayCurrency?.isin as FiatCode) || 'USD',
          },
          cryptoAmount: {
            amount: debitAmount,
            currency: defaultDebitWallet.code as CryptoCode,
          },
          currencyInfoInWallet: defaultDebitWallet,
        },
        to: {
          fiatAmount: {
            amount: creditFiatAmount,
            currency: (displayCurrency?.isin as FiatCode) || 'USD',
          },
          cryptoAmount: {
            amount: creditAmount,
            currency: defaultCreditWallet.code as CryptoCode,
          },
          currencyInfoInWallet: defaultCreditWallet,
        },
      })
    }

    trackEvent(events.MONEY_ACTION_STARTED, {
      action_type: 'convert',
      source: 'asset_screen', // Assuming entry via asset screen usually
    })
  }, [
    defaultDebitWallet,
    defaultCreditWallet,
    setConversion,
    setIsFiatMode,
    debitAmount,
    debitFiatAmount,
    creditAmount,
    creditFiatAmount,
    displayCurrency,
    trackEvent,
    events.MONEY_ACTION_STARTED,
  ])

  return (
    <BaseSafeFormView
      style={{ height: '100vh' }}
      flex={1}
      bgColor="background.screen"
    >
      {/* Fixed Header Navigation */}
      <StickyHeaderNavigation title={t('form:actions.convert')} />
      <BaseView
        px={13}
        data-component="conversion-screen"
        style={{
          height: '100vh',
          paddingTop: offsets.top + 70,
          justifyContent: 'space-between',
        }}
        flex={1}
      >
        {/* Scrollable Content */}
        <div
          data-component="conversion-screen-content"
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ paddingBottom: '80px' }}>
            <CryptoSwapInputs
              style={{
                marginTop: 4,
                marginBottom: 12,
              }}
            />
            <div style={{ paddingBottom: '24px' }}>
              <ConversionInfos />
            </div>
          </div>
        </div>
        <ConfirmationButton />
      </BaseView>
    </BaseSafeFormView>
  )
}

export default ConversionScreen
